import { Grid, List, Tooltip, Typography } from "@material-ui/core";
import {
  makeStyles,
  createStyles,
  Theme,
  withStyles,
} from "@material-ui/core/styles";
import { useAppDispatch, useAppSelector } from "../app/appHooks";
import { useTranslation } from "react-i18next";
import {
  CommunityTypes,
  RoleTypes,
  SettingListKeys,
} from "../../helpers/constants";
import React, { useMemo, useRef } from "react";
import MuiListItem from "@material-ui/core/ListItem";
import { addCommunityUser, setSettingsUser } from "./settingsSlice";
import { IUserDetails } from "../../services/header.services";
import {
  getResidentName,
  isActiveUser,
  invitationEmailFailed,
} from "../dashboard/dashboardSlice";
import useHelpAtHome from "../notifications/hooks/useHelpAtHome";
import clsx from "clsx";
import { AddNewItemButton } from "./common/AddNewItemButton";
import { getRoleLabel } from "./settingsHelpers";
import { sortUsers } from "./common/helpers";
import { DateTimeDiffHoursDays } from "../../helpers/datetime";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainContainer: {
      position: "relative",
      height: "100%",
      width: "50%",
    },
    userListContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      background: theme.palette.light.main,
      borderRight: "1px solid rgba(0, 0, 0, 0.12)",
      padding: theme.spacing(3, 0),
      overflowY: "auto",
      paddingBottom: theme.spacing(8),
    },
    listContainer: {
      background: "#FFF",
    },
    optionsContainer: {
      width: "100%",
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(3),
    },
    secondaryListContainer: {
      display: "flex",
      flex: 1,
      width: "100%",
      flexDirection: "column",
      padding: theme.spacing(0, 1),
    },
    primaryTextContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    },
    userRelation: {
      textAlign: "right",
      fontSize: "0.7rem",
      color: theme.palette.gray.main,
    },
    emailFailed: {
      color: theme.palette.error.main, // red
      fontSize: "0.58rem",
      paddingRight: "1px",
    },
    truncateEllipsis: {
      whiteSpace: "nowrap",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "-o-text-overflow": "ellipsis",
    },
    isPendingUser: {
      fontStyle: "italic",
    },
    emailText: {
      fontSize: theme.typography.caption.fontSize,
    },
    captionContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
    },
    nameRoleText: {
      textAlign: "right",
      paddingRight: "1px",
    },
    familyText: {
      fontSize: "8px",
      lineHeight: "0.25",
    },
  })
);

const UserSettingsListItem = withStyles(({ palette, spacing }) => ({
  root: {
    paddingBottom: spacing(1),
    paddingTop: spacing(1),
    width: "100%",
    borderRadius: "0px",
    "&$selected": {
      backgroundColor: `${palette.primary.main}4D !important `,
    },
    "&$selected:hover": {
      backgroundColor: palette.primary.main,
    },
    "&:hover": {
      backgroundColor: `${palette.primary.main}4D !important `,
    },
  },
  selected: {},
  divider: {},
}))(MuiListItem);

// Get a unit resident's name based on an array of IResidents
export const getUserName = <
  T extends {
    first_name?: string | null;
    last_name?: string | null;
  }
>(
  user?: T
) => {
  if (user === undefined) return "";

  let firstName =
    user.first_name !== null &&
    user.first_name !== undefined &&
    user.first_name !== ""
      ? user.first_name.split(" ")[0]
      : "";
  firstName = firstName.slice(0, 10);

  let lastName =
    user.last_name !== null &&
    user.last_name !== undefined &&
    user.last_name !== ""
      ? user.last_name
      : "";

  let fullName = "";
  if (firstName !== "") {
    fullName = firstName;
  }
  if (lastName !== "") {
    if (fullName !== "") {
      fullName = fullName.concat(" ", lastName);
    } else {
      fullName = lastName;
    }
  }
  return fullName;
};
const CustomTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}))(Tooltip);

const UserGroupList = (props: {
  users: IUserDetails[];
  isHelpAtHome: boolean;
  isAipCommunity: boolean;
  selectedUser?: string;
  handleSelectUser: (userID: string) => void;
}) => {
  const {
    users,
    isHelpAtHome,
    isAipCommunity,
    selectedUser,
    handleSelectUser,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <List className={classes.optionsContainer}>
      {users.map((user) => {
        const roleName = getRoleLabel(user?.role?.name);
        const userRelation = user.units?.reduce((prevVal, currentVal) => {
          if (isHelpAtHome) {
            return prevVal === ""
              ? getResidentName(currentVal.residents, {
                  fullFirstName: false,
                })
              : `${prevVal}, ${getResidentName(currentVal.residents, {
                  fullFirstName: false,
                })}`;
          } else {
            return prevVal === "" && currentVal.name !== null
              ? currentVal.name
              : `${prevVal}, ${currentVal.name}`;
          }
        }, "");

        const isPending = !isActiveUser(user);
        const emailFailed = invitationEmailFailed(user);
        const userName = !isPending ? getUserName(user) : "-";
        const timeDifference = user?.last_active_time
          ? DateTimeDiffHoursDays(user?.last_active_time, t)
          : "";
        const showSecondaryText =
          roleName === RoleTypes.family || isPending || timeDifference;
        const displayText = isPending
          ? emailFailed
            ? t("email_delivery_failed")
            : t("pending")
          : timeDifference;

        return (
          <UserSettingsListItem
            button
            key={user.id}
            divider
            disableGutters
            onClick={() => handleSelectUser(user.id)}
            selected={user.id === selectedUser}
            className={classes.listContainer}
          >
            <div
              className={clsx(
                classes.secondaryListContainer,
                isPending && classes.isPendingUser
              )}
            >
              <div className={classes.primaryTextContainer}>
                <Grid container>
                  <Grid item xs={8}>
                    <Typography variant="subtitle2">{userName}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.captionContainer}>
                      <Typography
                        variant="caption"
                        className={classes.familyText}
                      >
                        {roleName === RoleTypes.family &&
                        !isAipCommunity &&
                        userRelation !== ""
                          ? `${t(roleName)} ${t("of")}`
                          : ""}
                      </Typography>
                      {roleName !== "" && (
                        <>
                          {roleName !== RoleTypes.family || isAipCommunity ? (
                            <div className={classes.nameRoleText}>
                              <Typography variant="caption">
                                {t(roleName)}
                              </Typography>
                            </div>
                          ) : (
                            <div
                              className={clsx(
                                classes.truncateEllipsis,
                                classes.nameRoleText
                              )}
                            >
                              <Typography variant="caption">
                                {userRelation}
                              </Typography>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>

              <Grid container>
                <Grid item xs={showSecondaryText ? 8 : 12}>
                  <div
                    className={clsx(
                      classes.emailText,
                      classes.truncateEllipsis
                    )}
                  >
                    {user.email}
                  </div>
                </Grid>

                {showSecondaryText && (
                  <CustomTooltip
                    title={userRelation}
                    placement="top"
                    interactive
                    disableHoverListener={isPending}
                    disableFocusListener={isPending}
                    disableTouchListener={isPending}
                  >
                    <Grid item xs={4}>
                      <div
                        className={clsx(
                          classes.userRelation,
                          classes.truncateEllipsis
                        )}
                      >
                        {isPending && emailFailed ? (
                          <span className={classes.emailFailed}>
                            {t(displayText)}
                          </span>
                        ) : (
                          <span>{displayText}</span>
                        )}
                      </div>
                    </Grid>
                  </CustomTooltip>
                )}
              </Grid>
            </div>
          </UserSettingsListItem>
        );
      })}
    </List>
  );
};

export function UsersSettingsList() {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  /* Refs */
  const ref = useRef<HTMLDivElement>(null);

  /* Selectors */
  const selectedOption = useAppSelector(
    (state) => state.settingsState.selectedOption
  );

  const selectedUser = useAppSelector(
    (state) => state.settingsState.selectedUser
  );

  const users = useAppSelector((state) => state.headerState.users);
  const units = useAppSelector((state) => state.headerState.units);

  const [allHelpAtHome] = useHelpAtHome(units);

  const isAipCommunity = useAppSelector(
    (state) =>
      state.headerState.selectedCommunity?.community_type === CommunityTypes.aip
  );

  const isCaregiver = useAppSelector(
    (state) =>
      !!state.headerState.selectedCommunity?.role.includes(RoleTypes.caregiver)
  );

  /* Derived lists */
  const orderedUserGroups = useMemo(() => {
    if (users === undefined) return [];

    const adminList = sortUsers(
      users.filter((user) => user.role.name === RoleTypes.admin)
    );
    const caregiversList = sortUsers(
      users.filter((user) => user.role.name === RoleTypes.caregiver)
    );
    const installerList = sortUsers(
      users.filter((user) => user.role.name === RoleTypes.installer)
    );
    const familyList = sortUsers(
      users.filter(
        (user) =>
          user.role.name === RoleTypes.pro_family ||
          user.role.name === RoleTypes.family
      )
    );

    return isAipCommunity
      ? [adminList, familyList, caregiversList]
      : [adminList, caregiversList, familyList, installerList];
  }, [users, isAipCommunity]);

  /* Methods */
  const handleSelectUser = (userID: string) => {
    dispatch(setSettingsUser(userID));
  };
  const handleAddUser = () => {
    dispatch(addCommunityUser());
  };

  return (
    <React.Fragment>
      {selectedOption === SettingListKeys.community_users && (
        <div className={classes.mainContainer}>
          <div ref={ref} className={classes.userListContainer}>
            {users !== undefined && (
              <React.Fragment>
                {orderedUserGroups.map(
                  (group, index) =>
                    group.length > 0 && (
                      <UserGroupList
                        key={index}
                        users={group}
                        handleSelectUser={handleSelectUser}
                        isHelpAtHome={!!allHelpAtHome}
                        isAipCommunity={isAipCommunity}
                        selectedUser={selectedUser}
                      />
                    )
                )}
              </React.Fragment>
            )}
          </div>
          {!isCaregiver && (
            <AddNewItemButton parentRef={ref} handleAddAction={handleAddUser} />
          )}
        </div>
      )}
    </React.Fragment>
  );
}
